import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSnackbarController } from "@rebasepro/app";
import { Button, Card, Chip, cls, defaultBorderMixin, FilterChip, TextField, Typography } from "@rebasepro/ui";
import { inboxPath } from "rebase-leadgen-config";
import { client } from "../lib/rebaseClient";
import { describeError, fullStamp, sinceLabel } from "../inbox/shared";

/**
 * What happened with a person or a company, newest first — and a box to add
 * what the app cannot see for itself: a call, a meeting, a note.
 *
 * Append-only, like the collection it reads: a corrected row would be a
 * rewritten past. The `actor` on each row is the audit trail for the approval
 * gates — what the agent did, what a person here did, and what the other side
 * did are three different kinds of fact.
 */

type Activity = {
    id: string;
    kind?: string | null;
    summary?: string | null;
    detail?: string | null;
    actor?: string | null;
    occurred_at?: string | null;
    threadId?: string | null;
};

const KIND: Record<string, { label: string; color: string }> = {
    lead_found: { label: "Lead found", color: "grayLighter" },
    prospect_found: { label: "Found", color: "blueLighter" },
    email_drafted: { label: "Drafted", color: "purpleLighter" },
    email_sent: { label: "Email sent", color: "greenLighter" },
    reply_received: { label: "Replied", color: "tealLighter" },
    bounced: { label: "Bounced", color: "redLighter" },
    opted_out: { label: "Opted out", color: "redLighter" },
    status_changed: { label: "Status", color: "yellowLighter" },
    note: { label: "Note", color: "grayLighter" },
    call: { label: "Call", color: "blueLighter" },
    meeting: { label: "Meeting", color: "purpleLighter" }
};

const ACTOR: Record<string, string> = { agent: "the agent", human: "us", prospect: "them" };

const MANUAL: { id: "note" | "call" | "meeting"; label: string }[] = [
    { id: "note", label: "Note" },
    { id: "call", label: "Call" },
    { id: "meeting", label: "Meeting" }
];

export function Timeline({ prospectId, companyId }: { prospectId?: string | null; companyId?: string | null }) {
    const navigate = useNavigate();
    const snackbar = useSnackbarController();
    const [rows, setRows] = useState<Activity[] | null>(null);
    const [kind, setKind] = useState<"note" | "call" | "meeting">("note");
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const load = useCallback(async () => {
        const where = prospectId ? { prospectId: ["==", prospectId] } : companyId ? { companyId: ["==", companyId] } : null;
        if (!where) { setRows([]); return; }
        try {
            const { data } = await client.data.collection<Activity>("activities").find({ where: where as never, orderBy: ["occurred_at", "desc"], limit: 60 });
            setRows(data ?? []);
        } catch (err) {
            setError(describeError(err));
            setRows([]);
        }
    }, [prospectId, companyId]);

    useEffect(() => { void load(); }, [load]);

    const add = async () => {
        const body = text.trim();
        if (!body) return;
        setSaving(true);
        try {
            const first = body.split("\n")[0].slice(0, 200);
            await client.data.collection("activities").create({
                prospect: prospectId ?? null,
                company: companyId ?? null,
                kind,
                summary: first,
                detail: body.length > first.length ? body : null,
                actor: "human"
            });
            setText("");
            snackbar.open({ type: "success", message: `${MANUAL.find(m => m.id === kind)?.label} added to the timeline` });
            await load();
        } catch (err) {
            setError(describeError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <Typography variant="subtitle2">Timeline{rows?.length ? ` (${rows.length})` : ""}</Typography>

            <Card className={cls("p-3 flex flex-col gap-2", defaultBorderMixin)}>
                <div className="flex gap-1.5">
                    {MANUAL.map(option => (
                        <FilterChip key={option.id} size="small" active={kind === option.id} onClick={() => setKind(option.id)}>{option.label}</FilterChip>
                    ))}
                </div>
                <TextField
                    size="small"
                    multiline
                    minRows={2}
                    label="What happened"
                    value={text}
                    onChange={event => setText(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void add(); }
                    }}
                />
                <div>
                    <Button size="small" variant="outlined" disabled={saving || !text.trim()} onClick={() => void add()}>Add to the timeline</Button>
                </div>
            </Card>

            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {rows?.length === 0 && <Typography variant="body2" color="secondary">Nothing has happened here yet.</Typography>}

            <div className="flex flex-col gap-2">
                {(rows ?? []).map(row => {
                    const k = KIND[row.kind ?? ""] ?? { label: row.kind ?? "", color: "grayLighter" };
                    return (
                        <div key={row.id} className="flex gap-3">
                            <div className="shrink-0 pt-0.5"><Chip size="small" colorScheme={k.color as never}>{k.label}</Chip></div>
                            <div className="min-w-0 grow">
                                <Typography variant="body2" className="break-words">{row.summary}</Typography>
                                {row.detail && row.detail !== row.summary && (
                                    <Typography variant="body2" color="secondary" className="whitespace-pre-wrap break-words">{row.detail}</Typography>
                                )}
                                <Typography variant="caption" color="secondary" className="block">
                                    <span title={fullStamp(row.occurred_at)}>{sinceLabel(row.occurred_at)}</span>
                                    {row.actor ? ` · by ${ACTOR[row.actor] ?? row.actor}` : ""}
                                    {row.threadId && (
                                        <>
                                            {" · "}
                                            <button type="button" className="underline" onClick={() => navigate(inboxPath({ thread: row.threadId }))}>open the conversation</button>
                                        </>
                                    )}
                                </Typography>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
